import { Table, Typography } from 'antd';

import LinkToContactUs from '../../components/PrivacyAndTerms/LinkToContactUs';

import styles from './styles.module.scss';

const { Title } = Typography;

const columns = [
  {
    title: 'Cookie Type',
    dataIndex: 'type',
    key: 'type',
    ellipsis: true
  },
  {
    title: 'Purpose',
    dataIndex: 'purpose',
    key: 'purpose'
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    ellipsis: true
  },
  {
    title: 'Provider',
    dataIndex: 'provider',
    key: 'provider'
  }
];
const data = [
  {
    key: '1',
    type: 'Essential',
    purpose: 'Website functionality (e.g., session management, account access)',
    duration: 'Session',
    provider: 'HubSpot, Kraal Ltd'
  },
  {
    key: '2',
    type: 'Analytics',
    purpose: 'Usage analysis (e.g., page views, clicks) for service improvement',
    duration: 'Up to 12 months',
    provider: 'Google Analytics 4, Instagram, Facebook, LinkedIn, YouTube, X'
  },
  {
    key: '3',
    type: 'Marketing',
    purpose: 'Personalized content and advertisements for opted-in users',
    duration: 'Up to 18 months',
    provider: 'HubSpot, Instagram, Facebook, LinkedIn, YouTube, X'
  }
];
function LinkToTerms({ sectionId, sectionName }) {
  return (
    <a
      href={sectionId ? `/auth/terms#${sectionId}` : '/auth/terms'}
      target="_blank"
      rel="noopener noreferrer"
      className="font-bold">
      {sectionName ? `Terms and Conditions ${sectionName}` : 'Terms and Conditions'}
    </a>
  );
}

function PrivacyContent() {
  return (
    <div className={styles['privacy-terms-content']}>
      <Title level={1} className="mb-4">
        Privacy Policy
      </Title>
      <p className="font-bold">Kraal Ltd</p>
      <p className="font-bold">Effective Date: 15 October 2025</p>
      <p>
        Kraal Ltd (“we,” “us,” “our”), a company registered in England and Wales (Company No: 10361693) at 7 Bell Yard,
        Westminster, London, WC2A 2JR, United Kingdom, is committed to protecting the privacy and security of personal
        data. This Privacy Policy explains how we collect, use, share, and protect personal data in connection with our
        business-to-business (B2B) services, web application, and website (kraal.farm). Data from our IoT devices
        (Livestock Tracking and Monitoring Devices and LoRa Gateways) consists exclusively of non-personal animal and
        telemetry data, as outlined in our <LinkToTerms />, and is not subject to this policy under UK GDPR. This policy
        should be read alongside our <LinkToTerms /> for full context on data and service use.
      </p>
      <p>
        This policy complies with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
        It is reviewed annually or upon significant changes to ensure ongoing compliance.
      </p>
      <ol className={styles['ordered-list']}>
        <li>
          <Title level={3} className="mb-2">
            Scope of This Policy
          </Title>
          <p>This policy applies to:</p>
          <ul>
            <li>
              <strong>Authorized Users from Client Businesses</strong>: Employees or representatives of client
              organizations who subscribe to our services and use our web application for business purposes.
            </li>
            <li>
              <strong>Website Visitors</strong>: Individuals who visit our website (kraal.farm) and may opt-in to
              receive newsletters or exclusive content.
            </li>
          </ul>
          <p>
            This policy does not cover employee data, other internal processing activities, or non-personal animal and
            telemetry data processed by our IoT devices. If any IoT data becomes linked to an identifiable individual
            (e.g., via client uploads), it will be treated as personal data under this policy.
          </p>
        </li>
        <li id="personal-data">
          <Title level={3} className="mb-2">
            Personal Data We Collect
          </Title>
          <p>We collect the following categories of personal data:</p>
          <ul>
            <li>
              <strong>Identifiers</strong>: Names, surnames, business email addresses (which may identify individuals,
              including sole traders’ personal emails), business contact details, and address details (which may include
              personal addresses for sole traders or individuals within client organizations).
            </li>
            <li>
              <strong>Technical Data</strong>: IP addresses, device IDs, browser activity, and usage logs from our web
              application and website. Inferences from technical data (e.g., user behavior patterns) are anonymized to
              prevent identification.
            </li>
            <li>
              <strong>Payment Information</strong>: Transaction references processed via Stripe. We do not store payment
              card details.
            </li>
          </ul>
          <p>
            <strong>Non-Personal Data</strong>: Animal location data and telemetry data from Livestock Tracking and
            Monitoring Devices and LoRa Gateways are exclusively non-personal and not covered by this policy. See{' '}
            <LinkToTerms sectionId="user-content" sectionName="Section 8" /> for details.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            How We Collect Personal Data
          </Title>
          <p>We collect personal data:</p>
          <ul>
            <li>
              <strong>Directly</strong>: Via forms and account registration on our website, managed through HubSpot.
            </li>
            <li>
              <strong>Automatically</strong>: Through cookies, Google Analytics 4 (GA4), and tracking technologies
              (e.g., browser activity, page views) on our website and web application.
            </li>
            <li>
              <strong>From Third Parties</strong>: Limited to data shared by business clients (e.g., contact details of
              authorized users) or social media platforms (e.g., engagement data from Instagram, Facebook, LinkedIn,
              YouTube, X).
            </li>
          </ul>
        </li>
        <li id="purposes">
          <Title level={3} className="mb-2">
            Purposes and Legal Bases for Processing
          </Title>
          <p>We process personal data for the following purposes and legal bases:</p>
          <ul>
            <li>
              <strong>Service Delivery</strong>: To provide and manage our B2B services, including account setup and web
              application access (Legal Basis: Performance of a contract). See Terms and Conditions Section 6 for
              subscription and account obligations. For personal data uploaded by business clients to our platform, we
              act as a data processor, with clients as the data controller.
            </li>
            <li>
              <strong>Service Improvement</strong>: To analyze usage and troubleshoot issues with our web application
              (Legal Basis: Legitimate interests). We have conducted a Legitimate Interests Assessment (LIA) to ensure
              this does not override your rights.
            </li>
            <li>
              <strong>Marketing and Communications</strong>: To send newsletters or promotional content to opted-in
              website visitors and business users (Legal Basis: Consent). You may unsubscribe at any time via the link
              in our emails, impacting only future communications.
            </li>
            <li>
              <strong>Compliance</strong>: To meet legal and tax obligations, such as maintaining transaction records
              (Legal Basis: Legal obligation).
            </li>
          </ul>
          <p>Animal and telemetry data from IoT devices are exclusively non-personal and not subject to UK GDPR.</p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Data Sharing
          </Title>
          <p>
            We share personal data with the following third parties, under Data Processing Agreements (DPAs) compliant
            with UK GDPR Article 28:
          </p>
          <ul>
            <li>
              <strong>HubSpot, Inc.</strong>: For managing forms, account registration, and newsletters.
            </li>
            <li>
              <strong>Amazon Web Services (AWS)</strong>: For hosting our web application.
            </li>
            <li>
              <strong>Google Analytics 4 (GA4)</strong>: For analyzing website and app usage, with IP anonymization
              enabled.
            </li>
            <li>
              <strong>Stripe Payments Europe, Ltd.</strong>: For processing payments (Stripe acts as the data controller
              for payment data). See <LinkToTerms sectionId="subscriptions" sectionName="Section 6" /> for payment
              terms.
            </li>
            <li>
              <strong>Social Media Platforms (Instagram, Facebook, LinkedIn, YouTube, X)</strong>: For marketing and
              engagement analytics.
            </li>
          </ul>
          <p>
            We do not share personal data with other parties unless required by law. Non-personal animal and telemetry
            data from IoT devices may be processed by AWS for hosting but is not subject to this policy.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            International Data Transfers
          </Title>
          <p>
            Some third-party service providers (e.g., HubSpot, AWS, Google Analytics) may process personal data outside
            the UK. We ensure appropriate safeguards, such as Standard Contractual Clauses (SCCs) or International Data
            Transfer Agreements (IDTAs) approved by the UK Information Commissioner’s Office (ICO), or rely on adequacy
            decisions where applicable. Non-personal IoT data is not subject to these requirements.
          </p>
        </li>
        <li id="data-retention">
          <Title level={3} className="mb-2">
            Data Retention
          </Title>
          <p>We retain personal data as follows:</p>
          <ul>
            <li>
              <strong>Business User Data</strong>: For the duration of the contract and up to 6 years thereafter, to
              comply with tax and legal obligations (e.g., Limitation Act 1980). Retention may be extended for the
              duration of disputes or legal claims, as notified to you.
            </li>
            <li>
              <strong>Non-Customer Data (e.g., website visitors, newsletter subscribers)</strong>: Up to 18 months after
              last interaction or unsubscribe request.
            </li>
            <li>
              <strong>Technical Data</strong>: Up to 12 months for analytics and troubleshooting purposes.
            </li>
          </ul>
          <p>
            Data is securely deleted using industry-standard methods (e.g., overwriting) when no longer needed, in
            accordance with our retention policies. Non-personal IoT data retention is governed by{' '}
            <LinkToTerms sectionId="user-content" sectionName="Section 8" />.
          </p>
        </li>
        <li id="data-security">
          <Title level={3} className="mb-2">
            Data Security
          </Title>
          <p>We implement robust security measures for personal data, including:</p>
          <ul>
            <li>
              <strong>Encryption</strong>: Data at rest and in transit is encrypted using AES-256 and TLS 1.3,
              respectively.
            </li>
            <li>
              <strong>Access Controls</strong>: Role-based access and multi-factor authentication for our systems.
            </li>
            <li>
              <strong>Audits and Incident Response</strong>: Annual security audits and a defined incident response plan
              compliant with UK GDPR Article 32.
            </li>
          </ul>
          <p>
            Our IoT devices (Livestock Tracking and Monitoring Devices and LoRa Gateways) process only non-personal
            animal and telemetry data and comply with the Product Security and Telecommunications Infrastructure Act
            2022, ensuring secure data transmission. See{' '}
            <LinkToTerms sectionId="client-responsibilities" sectionName="Section 7" /> for client security obligations.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Cookies and Tracking Technologies
          </Title>
          <p>
            Our website and web application use cookies and similar technologies to enhance user experience, analyze
            performance, and deliver tailored content. We use a cookie consent banner (via HubSpot) to obtain your
            consent for non-essential cookies. You can manage preferences at any time via our cookie settings tool or
            contact us at <LinkToContactUs />. Below is a summary of the cookies we use:
          </p>
          <Table columns={columns} dataSource={data} size="small" pagination={false} bordered={true} className="mb-4" />
          <p>
            Essential cookies are necessary and cannot be disabled. Non-essential cookies require consent, and data may
            be transferred outside the UK under safeguards (see Section 6). For more details, see our cookie policy.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Your Data Protection Rights
          </Title>
          <p>Under UK GDPR, you have the following rights:</p>
          <ul>
            <li>
              <strong>Access</strong>: Request a copy of your personal data.
            </li>
            <li>
              <strong>Rectification</strong>: Correct inaccurate data.
            </li>
            <li>
              <strong>Erasure</strong>: Request deletion (subject to legal obligations).
            </li>
            <li>
              <strong>Restriction</strong>: Limit processing in certain cases.
            </li>
            <li>
              <strong>Objection</strong>: Object to processing based on legitimate interests or marketing.
            </li>
            <li>
              <strong>Data Portability</strong>: Receive your data in a structured, machine-readable format.
            </li>
          </ul>
          <p>
            To exercise these rights, contact us at <LinkToContactUs /> with verification details (e.g., account email).
            We will respond within one month, subject to legal exemptions. For business users, we handle requests
            directly unless redirected to client controllers (per{' '}
            <LinkToTerms sectionId="data-protection" sectionName="Section 16" />
            ). If you have concerns, you may lodge a complaint with the ICO:{' '}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="font-bold">
              https://ico.org.uk
            </a>
            .
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            No Children’s Data
          </Title>
          <p>
            We do not process personal data relating to children under 13, as our services are designed for business
            use.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            No Automated Decision-Making or Profiling
          </Title>
          <p>
            We do not engage in automated decision-making or profiling that produces legal or similarly significant
            effects. Anonymised analytics may be used to improve platform performance, as noted in{' '}
            <LinkToTerms sectionId="user-content" sectionName="Section 8" />.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Links to Terms and Conditions
          </Title>
          <p>
            Our data processing practices are further governed by our <LinkToTerms />, which outline your obligations
            and rights as a business user, including the use of IoT devices for non-personal animal data. Please review
            them for details on service usage and data handling.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Contact Us
          </Title>
          <p className="mb-0">For questions or concerns, contact:</p>
          <p className="mb-0 font-bold">Kraal Ltd</p>
          <p className="mb-0">7 Bell Yard, Westminster, London, WC2A 2JR, United Kingdom</p>
          <p>
            Email: <LinkToContactUs />
          </p>
          <p>
            Business clients may request a Data Processing Agreement (DPA) compliant with UK GDPR Article 28 to clarify
            roles and responsibilities. We are not required to appoint a Data Protection Officer under UK GDPR.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Updates to This Policy
          </Title>
          <p>
            We may update this policy to reflect changes in our practices or legal requirements. Updates will be posted
            on our website with a revised effective date and notified via email or in-app where applicable.
          </p>
        </li>
      </ol>
    </div>
  );
}

export default PrivacyContent;
